// Role/privilege helpers for organizer JWT claim: `role` (integer bitmask).

export const PRIVILEGE = {
  EVENTS: 'events',
  RIDES: 'rides',
  VOUCHERS: 'vouchers',
  REPORTING: 'reporting',
  FINANCE: 'finance',
  GLOBAL_ADMIN: 'global_admin',
};

// Bit positions map directly to 2^N (see ROLE_BASED_ACCESS.md).
export const PRIVILEGE_BIT_POSITION = {
  [PRIVILEGE.EVENTS]: 11,
  [PRIVILEGE.RIDES]: 12,
  [PRIVILEGE.VOUCHERS]: 13,
  [PRIVILEGE.REPORTING]: 14,
  [PRIVILEGE.FINANCE]: 15,
  [PRIVILEGE.GLOBAL_ADMIN]: 16,
};

const GLOBAL_ADMIN_THRESHOLD = 2n ** 16n; // 65536

export function parseRole(value) {
  // JWT claim may be number or string; use BigInt to avoid JS 32-bit bitwise limits.
  if (value === null || value === undefined) return 0n;
  try {
    // Accept numeric strings too.
    return BigInt(value);
  } catch (e) {
    return 0n;
  }
}

export function isGlobalAdmin(roleValue) {
  const role = parseRole(roleValue);
  // Spec: "If role is 2^16 or greater, the user has all privileges."
  return role >= GLOBAL_ADMIN_THRESHOLD;
}

export function hasPrivilege(roleValue, privilege) {
  const role = parseRole(roleValue);
  if (isGlobalAdmin(role)) return true;
  const pos = PRIVILEGE_BIT_POSITION[privilege];
  if (pos === undefined) return false;
  const mask = 2n ** BigInt(pos);
  return (role / mask) % 2n === 1n;
}

export function setPrivilege(roleValue, privilege, enabled) {
  const role = parseRole(roleValue);
  const pos = PRIVILEGE_BIT_POSITION[privilege];
  if (pos === undefined) return role;
  const mask = 2n ** BigInt(pos);
  const isSet = (role / mask) % 2n === 1n;
  if (enabled) return isSet ? role : role + mask;
  return isSet ? role - mask : role;
}

export function roleToNumber(roleValue) {
  // Backend expects integer. If role exceeds JS safe integer, we still stringify.
  const role = parseRole(roleValue);
  const asNumber = Number(role);
  if (Number.isSafeInteger(asNumber)) return asNumber;
  return role.toString();
}
